import { t, Trans } from "@lingui/macro";
import { Brain, Browsers, Check, RocketLaunch, Sparkle } from "@phosphor-icons/react";
import { defaultResumeData } from "@stylingcv/schema";
import {
  AspectRatio,
  Badge,
} from "@stylingcv/ui";
import { cn, templatesList } from "@stylingcv/utils";
import { motion } from "framer-motion";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AiTemplateBanner } from "@/client/components/smart-ai/ai-template-banner";
import { VerifySkillsPrompt } from "@/client/components/verification-badge";
import { useTemplateRecommendations } from "@/client/hooks/smart-ai/use-template-recommendations";
import { useTemplateAccess } from "@/client/hooks/use-template-access";
import { useBuilderStore } from "@/client/stores/builder";
import { useResumeStore } from "@/client/stores/resume";
import { trackTemplateSelected } from "@/client/utils/gtm";


// Templates that support cover letter mode (limited selection for optimal cover letter presentation)
const COVER_LETTER_TEMPLATES = ["clean", "classic"];

// Template categories for better organization
const templateCategories = {
  professional: [
    "classic",
    "confidence",
    "onyx",
    "nexus",
    "leafish",
    "executive",
    "modern",
    "vibrant",
    "compact",
    "traditional",
    "rayhan",
    "pinnacle",
    "elegance",
    "folio",
  ],
  creative: ["highlight", "bold", "framed", "feminine", "slate", "zenith", "axiom", "vortex", "editorial", "architect", "studio", "corsa"],
  modern: [
    "essential",
    "clean",
    "split",
    "grid",
    "metro",
    "horizon",
    "sidebar",
    "accent",
    "prism",
    "modernist",
    "cipher",
    "apex",
  ], // removed "tech"
  minimal: ["simple", "straight", "minimal", "pure", "ethereal", "aura"],
};

const getTemplateCategory = (templateName: string) => {
  for (const [category, templates] of Object.entries(templateCategories)) {
    if (templates.includes(templateName)) {
      return category;
    }
  }
  return "professional";
};

const getCategoryColor = (category: string) => {
  const colors = {
    professional: "bg-blue-50 text-blue-700",
    creative: "bg-purple-50 text-purple-700",
    modern: "bg-green-50 text-green-700",
    minimal: "bg-gray-50 text-gray-700",
  };
  return colors[category as keyof typeof colors] || colors.professional;
};

type TemplateCardProps = {
  templateName: string;
  templateTheme?: { isFree?: string };
  index: number;
  isSelected: boolean;
  isAiRecommended: boolean;
  category: string;
  onSelect: (name: string) => void;
  onFocus?: () => void;
};

const TemplateCard = memo(
  ({
    templateName,
    templateTheme,
    index,
    isSelected,
    isAiRecommended,
    category,
    onSelect,
    onFocus,
  }: TemplateCardProps) => {
    const { isPremiumTemplate } = useTemplateAccess(templateTheme);
    const [isHovered, setIsHovered] = useState(false);
    const formatName = (name: string) => name.charAt(0).toUpperCase() + name.slice(1);

    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0, transition: { delay: index * 0.05 } }}
        whileHover={{ y: -4, transition: { duration: 0.2 } }}
        whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
        role="option"
        aria-selected={isSelected}
        aria-label={`${formatName(templateName)} template, ${category} category${isPremiumTemplate ? ", premium" : ""}${isAiRecommended ? ", AI recommended" : ""}`}
        tabIndex={0}
        className={cn(
          "group relative cursor-pointer overflow-hidden border-2 transition-all duration-300",
          "bg-white shadow-md hover:shadow-2xl",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#268599] focus-visible:ring-offset-2",
          isSelected
            ? "ring-2 ring-[#268599] border-[#268599]"
            : "border border-gray-200 hover:border-[#268599]/40",
          isAiRecommended && !isSelected && "",
        )}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={onFocus}
        onClick={() => onSelect(templateName)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(templateName);
          }
        }}
      >
        <div className="relative m-1 overflow-hidden">
          <AspectRatio ratio={1 / 1.4142} className="bg-gray-50">
            <motion.img
              src={`/templates/png/${templateName}.png`}
              alt={templateName}
              className={cn(
                "size-full object-cover transition-transform duration-300",
                isHovered && "scale-101",
              )}
              loading="lazy"
            />
            <div
              className={cn(
                "absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 transition-opacity duration-300",
                (isHovered || isSelected) && "opacity-100",
              )}
            />
            {isSelected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute right-3 top-3 z-20"
              >
                <div className="flex size-8 items-center justify-center rounded-full bg-black shadow-lg">
                  <Check size={16} className="text-white" weight="bold" />
                </div>
              </motion.div>
            )}
            {isPremiumTemplate && (
              <motion.div
                initial={{ scale: 0, rotate: -45 }}
                animate={{ scale: 1, rotate: 0 }}
                className="absolute right-3 top-3 z-20"
              >
                <div className="flex size-8 items-center justify-center rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 shadow-lg">
                  <RocketLaunch size={16} className="text-[#FFD700]" weight="fill" />
                </div>
              </motion.div>
            )}
            {isAiRecommended && !isSelected && (
              <motion.div
                initial={{ scale: 0, y: -10 }}
                animate={{ scale: 1, y: 0 }}
                className="absolute left-3 top-3 z-20 hidden"
              >
                <div className="flex items-center gap-1 rounded-full bg-blue-500 px-2 py-1 shadow-lg">
                  <Sparkle size={12} className="text-white" weight="fill" />
                  <span className="text-xs font-medium text-white">AI Pick</span>
                </div>
              </motion.div>
            )}
          </AspectRatio>
        </div>

        <div className="space-y-2 p-3">
          <div className="flex items-center justify-between">
            <h3
              className={cn(
                "text-ld flex items-center gap-1 font-bold transition-colors",
                isSelected
                  ? "text-[#268599]"
                  : "text-[#16344c] group-hover:text-[#268599]",
              )}
            >
              {isPremiumTemplate ? (
                <RocketLaunch size={14} className="text-[#FFD700]" weight="fill" />
              ) : (
                ""
              )}
              {formatName(templateName)}
            </h3>
            <Badge
              outline
              variant="primary"
              className={cn(
                "border-0 px-2 py-1 text-xs font-medium capitalize",
                getCategoryColor(category),
              )}
            >
              {category}
            </Badge>
          </div>
          <div className="flex items-center justify-between text-sm text-gray-500">
            {isAiRecommended && (
              <span className="flex hidden items-center gap-1 text-blue-600">
                <Brain size={14} weight="fill" />
                <Trans>
                  <Trans>Recommended</Trans>
                </Trans>
              </span>
            )}
          </div>
        </div>

        {isSelected && (
          <motion.div
            layoutId="selectedTemplate"
            className="pointer-events-none absolute inset-0 border-2 border-[#268599]"
            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
          />
        )}
      </motion.div>
    );
  },
);

TemplateCard.displayName = "TemplateCard";

type TemplateGridProps = {
  availableTemplates: string[];
  selectedTemplate: string;
  templateMetadata: Record<string, any>;
  isRecommendedTemplate: (name: string) => boolean;
  onTemplateSelect: (name: string) => void;
};

const TemplateGrid = memo(
  ({
    availableTemplates,
    selectedTemplate,
    templateMetadata,
    isRecommendedTemplate,
    onTemplateSelect,
  }: TemplateGridProps) => {
    const gridRef = useRef<HTMLDivElement>(null);
    const [focusedIndex, setFocusedIndex] = useState(-1);

    const handleGridKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        const total = availableTemplates.length;
        if (total === 0) return;

        const gridEl = gridRef.current;
        if (!gridEl) return;

        const cols = Math.round(gridEl.offsetWidth / (gridEl.firstElementChild as HTMLElement)?.offsetWidth) || 1;

        let nextIndex = focusedIndex;

        switch (e.key) {
          case "ArrowRight":
            e.preventDefault();
            nextIndex = focusedIndex < total - 1 ? focusedIndex + 1 : 0;
            break;
          case "ArrowLeft":
            e.preventDefault();
            nextIndex = focusedIndex > 0 ? focusedIndex - 1 : total - 1;
            break;
          case "ArrowDown":
            e.preventDefault();
            nextIndex = focusedIndex + cols < total ? focusedIndex + cols : focusedIndex;
            break;
          case "ArrowUp":
            e.preventDefault();
            nextIndex = focusedIndex - cols >= 0 ? focusedIndex - cols : focusedIndex;
            break;
          case "Home":
            e.preventDefault();
            nextIndex = 0;
            break;
          case "End":
            e.preventDefault();
            nextIndex = total - 1;
            break;
          default:
            return;
        }

        setFocusedIndex(nextIndex);
        const cards = gridEl.querySelectorAll<HTMLElement>("[role='option']");
        cards[nextIndex]?.focus();
      },
      [availableTemplates.length, focusedIndex],
    );

    return (
      <div
        ref={gridRef}
        role="listbox"
        aria-label={t`Template selection`}
        className="grid grid-cols-2 gap-4 @md/right:grid-cols-2 @lg/right:grid-cols-3"
        onKeyDown={handleGridKeyDown}
      >
        {availableTemplates.map((templateName, index) => (
            <TemplateCard
              key={templateName}
              templateName={templateName}
              templateTheme={templateMetadata[templateName]?.theme as { isFree?: string } | undefined}
              index={index}
              isSelected={templateName === selectedTemplate}
              isAiRecommended={isRecommendedTemplate(templateName)}
              category={getTemplateCategory(templateName)}
              onSelect={onTemplateSelect}
              onFocus={() => setFocusedIndex(index)}
            />
          ))}
      </div>
    );
  },
);

TemplateGrid.displayName = "TemplateGrid";

export const TemplateSection = () => {
  const navigate = useNavigate();
  const setValue = useResumeStore((state) => state.setValue);
  const setLeftPanelMode = useBuilderStore((state) => state.setLeftPanelMode);
  const template = useResumeStore((state) => {
    try {
      return state.resume.data.metadata.template || defaultResumeData.metadata.template;
    } catch {
      return defaultResumeData.metadata.template;
    }
  });

  // Get document type to determine if this is a cover letter
  const documentType = useResumeStore((state) => state.resume.type);
  const isCoverLetter = documentType === "coverLetter";

  // Filter templates based on document type
  const availableTemplates = useMemo(() => {
    if (isCoverLetter) {
      return templatesList.filter((t) => COVER_LETTER_TEMPLATES.includes(t));
    }
    return templatesList;
  }, [isCoverLetter]);

  // Get resume data for AI recommendations
  const resumeData = useResumeStore((state) => state.resume.data);
  const jobRole = resumeData?.basics?.headline || "";
  const workExperience = resumeData?.sections?.experience?.items || [];
  const yearsOfExperience = workExperience.length > 0 ? workExperience.length * 2 : 0; // Simple estimation

  // Get the current template icon from metadata (template is just a string name)
  const templateIcon = "";

  // State to store template metadata for each template
  const [templateMetadata, setTemplateMetadata] = useState<Record<string, any>>({});

  // Load template metadata for available templates
  useEffect(() => {
    const loadTemplateMetadata = async () => {
      const metadata: Record<string, any> = {};

      for (const templateName of availableTemplates) {
        try {
          const response = await fetch(`/templates/json/${templateName}.json`);
          if (response.ok) {
            const data = await response.json();
            metadata[templateName] = data.metadata || {};
          }
        } catch (error) {
          console.warn(`Failed to load metadata for template ${templateName}:`, error);
        }
      }

      setTemplateMetadata(metadata);
    };

    loadTemplateMetadata();
  }, [availableTemplates]);

  const updateTemplate = (templateName: string) => {
    try {
      const category = getTemplateCategory(templateName);

      // GTM tracking
      trackTemplateSelected({
        templateName,
        templateCategory: category,
        source: "builder",
      });

      setValue("metadata.template", templateName);

      const templateData = templateMetadata[templateName];
      if (templateData?.theme?.isFree !== undefined) {
        const isFreeRaw = templateData.theme.isFree as unknown;
        const normalizedIsFree =
          typeof isFreeRaw === "boolean" ? (isFreeRaw ? "YES" : "NO") : String(isFreeRaw);
        setValue("metadata.theme.isFree", normalizedIsFree);
      }

      const templateLayout = templateData?.layout;
      if (templateLayout && Array.isArray(templateLayout)) {
        const currentLayout = useResumeStore.getState().resume.data.metadata.layout;

        const templateSectionIds = new Set(templateLayout.flat(3));

        const currentSectionIds: string[] = [];
        if (currentLayout) {
          for (const page of currentLayout) {
            for (const column of page) {
              for (const sectionId of column) {
                currentSectionIds.push(sectionId);
              }
            }
          }
        }

        const customSections = currentSectionIds.filter((id) => !templateSectionIds.has(id));

        const newLayout = JSON.parse(JSON.stringify(templateLayout));

        if (customSections.length > 0 && newLayout[0]?.[0]) {
          newLayout[0][0].push(...customSections);
        }

        setValue("metadata.layout", newLayout);
      }
    } catch (error) {
      console.error("[Template Selection] Error updating template:", error);
    }
  };

  const updateColor = (color: string) => {
    try {
      setValue("metadata.theme.primary", color);
    } catch {
      // Silent fail
    }
  };

  // Get AI recommendations for template badges
  const { recommendations } = useTemplateRecommendations({
    jobRole,
    yearsOfExperience,
  });

  const isRecommendedTemplate = (templateName: string) => {
    return recommendations.some((rec) => rec.templateName === templateName);
  };


  // Handle navigation to interview
  const handleStartInterview = useCallback(() => {
    navigate("/interview", {
      state: {
        resumeData,
        source: "builder-verification",
      },
    });
  }, [navigate, resumeData]);

  return (
    <>
      {/* AI Template Recommendations Banner - only for resumes, not cover letters */}
      {!isCoverLetter && (
        <AiTemplateBanner
          jobRole={jobRole}
          yearsOfExperience={yearsOfExperience}
          currentTemplate={template}
          onTemplateSelect={updateTemplate}
          onColorSelect={updateColor}
        />
      )}

      <section id="template" className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-gray-200 pb-5">
          <div className="flex size-10 items-center justify-center rounded-xl bg-gray-100">
            <Browsers size={20} className="text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-[#16344c]">{t`Templates`}</h3>
            <p className="text-[11px] text-gray-400">{t`Choose your perfect design`}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {isCoverLetter && (
              <Badge outline className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                Cover Letter
              </Badge>
            )}
            <Badge variant="secondary" className="text-xs">
              {availableTemplates.length} {t`templates`}
            </Badge>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <main>
          {isCoverLetter && (
            <p className="mb-4 text-sm text-purple-600">
              {t`Showing templates optimized for cover letters (single-column layout)`}
            </p>
          )}
          <TemplateGrid
            availableTemplates={availableTemplates as string[]}
            selectedTemplate={template}
            templateMetadata={templateMetadata}
            isRecommendedTemplate={isRecommendedTemplate}
            onTemplateSelect={updateTemplate}
          />
        </main>
        </div>
      </section>
    </>
  );
};
